import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Modal } from "@shared/components/ui/Modal";
import { useGenerateQuote } from "@shared/hooks/useQuotes";
import type { CartItem } from "@public/stores/cartStore";
import { useCartStore } from "@public/stores/cartStore";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

// Función para extraer texto plano de HTML (igual que en Cart)
const stripHtmlTags = (html: string): string => {
  if (!html) return "";
  // Crear un elemento temporal para extraer el texto
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Esquema de validación con Zod
// Validamos que haya items en el carrito (aunque no haya campos de formulario)
// La validación real se hace en el componente (cartItems.length > 0)
const quoteSchema = z.object({
  // No hay campos, solo usamos react-hook-form para manejar el submit
});

type QuoteFormData = z.infer<typeof quoteSchema>;

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
}

export function QuoteModal({ isOpen, onClose, cartItems }: QuoteModalProps) {
  const [error, setError] = useState<string | null>(null);
  const clearCart = useCartStore((state) => state.clearCart);
  const closeCart = useCartStore((state) => state.closeCart);
  
  const {
    handleSubmit,
    reset,
  } = useForm<QuoteFormData>({
    resolver: zodResolver(quoteSchema),
    mode: "onChange",
  });

  const { mutate: generateQuote, isPending, isError, isSuccess, data: quoteData, reset: resetMutation } = useGenerateQuote();

  // Resetear estado cuando se abre o cierra el modal
  useEffect(() => {
    if (isOpen) {
      // Resetear cuando se abre el modal
      setError(null);
      reset();
      resetMutation();
    } else {
      // Resetear cuando se cierra el modal
      setError(null);
      reset();
      resetMutation();
    }
  }, [isOpen, reset, resetMutation]);

  // No usar window.open automáticamente - mejor mostrar un botón para que el usuario haga clic
  // Esto evita que los navegadores bloqueen la ventana emergente

  // Convertir CartItem a QuoteItem
  const convertCartItemsToQuoteItems = (items: CartItem[]) => {
    return items.map((item) => {
      // Extraer slug del linkUrl (/product/slug o /producto/slug -> slug)
      // Eliminar cualquier prefijo y quedarse solo con el slug
      let slug = item.linkUrl
        .replace(/^\/product\//, "")
        .replace(/^\/producto\//, "")
        .replace(/\/$/, ""); // Eliminar barra final si existe
      
      // Si no se pudo extraer el slug, usar el productId como fallback
      if (!slug || slug === item.linkUrl) {
        slug = item.productId;
      }
      
      return {
        productId: item.productId,
        productName: item.name,
        productSlug: slug,
        quantity: item.quantity,
        imageUrl: item.images?.[0]?.url || item.img || undefined,
        description: item.description ? stripHtmlTags(item.description) : undefined,
        unitOfMeasurement: item.unitOfMeasurement || undefined,
        brand: item.brand || undefined,
      };
    });
  };

  const onSubmit = () => {
    // Validar que haya items en el carrito
    if (cartItems.length === 0) {
      setError("El carrito debe tener al menos un producto");
      return;
    }

    // Validar que todas las cantidades sean mayores a 0
    const invalidItems = cartItems.filter((item) => item.quantity <= 0);
    if (invalidItems.length > 0) {
      setError("Todos los productos deben tener una cantidad mayor a 0");
      return;
    }

    setError(null);

    // Convertir items del carrito a items de cotización
    const quoteItems = convertCartItemsToQuoteItems(cartItems);

    // Generar cotización (sin datos de cliente por ahora)
    generateQuote(
      {
        items: quoteItems,
      },
      {
        onError: (error) => {
          setError(error.message || "Error al generar la cotización. Por favor, intente nuevamente.");
        },
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      // Resetear todo el estado antes de cerrar
      setError(null);
      reset();
      resetMutation();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generar Cotización"
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Estado de éxito */}
        {isSuccess && quoteData?.whatsappUrl && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  ¡Cotización generada exitosamente!
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Haz clic en el botón para abrir WhatsApp y enviar tu cotización.
                </p>
              </div>
            </div>
            <a
              href={quoteData.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                clearCart();
                closeCart();
                onClose();
              }}
              className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-[#25D366] rounded-lg hover:bg-[#20BA5A] transition-colors shadow-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Abrir WhatsApp
            </a>
          </div>
        )}

        {/* Estado de error */}
        {(isError || error) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                Error al generar la cotización
              </p>
              <p className="text-sm text-red-600 mt-1">
                {error || "Por favor, intente nuevamente."}
              </p>
            </div>
          </div>
        )}

        {/* Resumen de productos */}
        {!isSuccess && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Se generará una cotización con los siguientes productos:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
              {cartItems.map((item) => (
                <div
                  key={item.productId}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-700">{item.name}</span>
                  <span className="text-gray-600 font-medium">
                    {item.quantity} {item.unitOfMeasurement || "unidades"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensaje informativo */}
        {!isSuccess && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Al generar la cotización, aparecerá un botón para abrir WhatsApp y enviar tu solicitud.
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex items-center justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isPending || isSuccess || cartItems.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-[#282828] rounded-lg hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isPending ? "Generando..." : isSuccess ? "Generado" : "Generar Cotización"}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

